import { useEffect } from 'react'

import { getConversation } from 'conversations/modules/conversation/selectors'
import {
  getOrgChannel,
  getSocketSetupComplete,
  getUserSubscriptionChannel,
} from 'conversations/modules/socket/selectors'
import { getTeams } from 'conversations/modules/teams/selectors'
import { getAllConversationSections } from 'conversations/modules/user/sections/selectors'
import { getCurrentUserId } from 'conversations/modules/user/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { SharedSectionChangeConfig } from 'shared/src/typings/conversationsSidebar/Filters'
import { receiveSubscriptionEvent } from 'shared/store/actions/conversation'
import { fetchSectionsAsync, SharedSectionChangePayload } from 'shared/store/actions/user/sections'
import Conversation from 'shared/typings/conversations/Conversation'

import { getDriftWebSocket } from '@driftt/drift-ws'
import { GlobalStore as store } from '@driftt/traffic-control'

import { RootAction, RootState } from 'typesafe-actions'

export const useSectionSubscriptionChannel = (subscriptionId?: string, doSubscribe = true) => {
  const socketSetupComplete = useSelector<RootState, boolean>(getSocketSetupComplete)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const channelName = useSelector(getUserSubscriptionChannel(subscriptionId))

  useEffect(() => {
    if (doSubscribe && socketSetupComplete && subscriptionId) {
      const ws = getDriftWebSocket()

      const sectionSubscriptionSub = ws
        .on<{ conversation: Conversation; eventType: string }>({
          cluster: 'chat',
          channelName: channelName,
          messageName: 'subscription-event',
        })
        .subscribe((msg) => {
          const { conversation } = msg
          const storedConversation = getConversation(conversation.id)(store.getState())
          if (
            !storedConversation ||
            (storedConversation && storedConversation.updatedAt <= conversation.updatedAt)
          ) {
            dispatch(receiveSubscriptionEvent({ ...msg, subscriptionId }))
          }
        })

      return () => {
        sectionSubscriptionSub.unsubscribe()
      }
    }
  }, [channelName, dispatch, doSubscribe, socketSetupComplete, subscriptionId])
}

export const useSharedSectionSubscriptionChannel = () => {
  const socketSetupComplete = useSelector<RootState, boolean>(getSocketSetupComplete)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const channelName = useSelector(getOrgChannel)
  const currentUserId = useSelector(getCurrentUserId)
  const currentUsersTeams = useSelector(getTeams).filter((team) =>
    team.members.includes(currentUserId ?? 0)
  )
  const allSections = useSelector(getAllConversationSections)

  useEffect(() => {
    if (socketSetupComplete) {
      const ws = getDriftWebSocket()

      const sectionChangeSub = ws
        .on<SharedSectionChangePayload>({
          cluster: 'chat',
          channelName: channelName,
          messageName: 'resource-change',
        })
        .subscribe((msg) => {
          if (msg.resource) {
            const entityId = msg.resource.entityId
            const entityType = msg.resource.entityType
            const eventType = msg.eventType
            let subscriptionId: string | undefined = undefined

            //message blob can look different, make sure we get subscriptionId correctly
            subscriptionId = entityType ? msg.resource.subscriptionId : msg.resource.id
            const section = Object.values(allSections).find(
              (section) => section.subscriptionId === subscriptionId
            )
            const prevSectionData: SharedSectionChangeConfig = {
              section,
              subscriptionId,
              eventType,
              entityType,
              currentUserId,
              currentUsersTeams,
            }
            //if section exists, we know the current user is subscribed to it
            const isUpdate = eventType === 'UPDATE' && section
            const isSubscriptionMembershipCreateOrDelete =
              entityId === currentUserId || currentUsersTeams.some((team) => team.id === entityId)
            if (isUpdate || isSubscriptionMembershipCreateOrDelete) {
              dispatch(fetchSectionsAsync.request(prevSectionData))
            }
          }
        })

      return () => {
        sectionChangeSub.unsubscribe()
      }
    }
  }, [channelName, dispatch, socketSetupComplete, currentUserId, currentUsersTeams, allSections])
}
